import { useCallback, useEffect, useState } from "react";
import api, { getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lock } from "lucide-react";

// The engagement's methodology pin (B-446). Seeding procedures resolves the pinned version id only, never "the
// latest Published", so an engagement without a pin cannot get its audit program. The pin itself is one-time and
// immutable; the server decides who may set it (methodology roles) and refuses anything but a Published version.
export default function MethodologyPin({ id, onPinned }) {
  const [eng, setEng] = useState(null);
  const [versions, setVersions] = useState([]);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/engagements/${id}`).then((r) => setEng(r.data || {})).catch(() => setEng({}));
    api.get("/program-library/versions")
      .then((r) => setVersions((Array.isArray(r.data) ? r.data : []).filter((v) => v.status === "Published")))
      .catch(() => setVersions([]));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!eng) return null;
  if (eng.program_version_id) {
    return (
      <div className="bg-white border border-border rounded-sm p-3 text-xs flex flex-wrap items-center gap-2" data-testid="ewp-methodology-pinned">
        <Lock size={13} />
        <span>Metodologi perikatan: <b>{eng.methodology_version}</b></span>
        {eng.methodology_pinned_by_name && <span className="text-muted-foreground">· di-pin oleh {eng.methodology_pinned_by_name}{eng.methodology_pinned_at ? ` pada ${String(eng.methodology_pinned_at).slice(0, 10)}` : ""}</span>}
        <span className="text-muted-foreground">· tidak dapat diubah; prosedur di-seed dari versi ini.</span>
      </div>
    );
  }
  const pin = async () => {
    if (!choice) return toast.error("Pilih versi metodologi Published.");
    setBusy(true);
    try {
      const { data } = await api.post(`/engagements/${id}/methodology/pin`, { program_version_id: choice });
      toast.success(`Metodologi ${data.methodology_version} di-pin ke perikatan`);
      load(); onPinned?.();
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-amber-50 border border-amber-300 rounded-sm p-3 text-xs space-y-2" data-testid="ewp-methodology-unpinned">
      <div className="font-semibold text-amber-900">Perikatan ini belum memiliki versi metodologi yang di-pin.</div>
      <div className="text-amber-900">Prosedur hanya dapat di-seed dari versi yang di-pin. Pin bersifat permanen untuk perikatan ini dan hanya dapat dilakukan oleh peran metodologi (Super Admin, Firm Admin, Managing Partner, Quality Management Partner).</div>
      {versions.length === 0
        ? <div className="text-amber-900" data-testid="ewp-pin-none">Belum ada versi metodologi Published di Pustaka Program.</div>
        : <div className="flex flex-wrap items-center gap-2">
            <select aria-label="Versi metodologi Published" className="border border-border rounded-sm h-8 px-2 bg-white"
              value={choice} onChange={(e) => setChoice(e.target.value)} data-testid="ewp-pin-select">
              <option value="">Pilih versi Published</option>
              {versions.map((v) => <option key={v.id} value={v.id}>{v.methodology_version}</option>)}
            </select>
            <Button size="sm" disabled={busy} onClick={pin} data-testid="ewp-pin-btn">Pin ke perikatan</Button>
          </div>}
    </div>
  );
}
